import { useCallback, useEffect, useRef, useState } from 'react'
import { userSummaryApi } from '@/lib/userSummaryApi'
import type { AnchorUserChatSummary } from '@/types'

export function useUserChatSummary(userId?: string) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [summary, setSummary] = useState<AnchorUserChatSummary | null>(null)
  const requestGateRef = useRef(createSummaryRequestGate())

  useEffect(() => {
    requestGateRef.current.invalidate()
    setOpen(false)
    setLoading(false)
    setError('')
    setSummary(null)
  }, [userId])

  const showSummary = useCallback(() => {
    const requestSeq = requestGateRef.current.next()
    setOpen(true)
    if (!userId) {
      setLoading(false)
      setSummary(null)
      setError('No summary available')
      return
    }
    setLoading(true)
    setError('')
    userSummaryApi.getChatSummary(userId)
      .then((resp) => {
        if (!requestGateRef.current.isCurrent(requestSeq)) {
          return
        }
        setSummary(resp.data)
        setError('')
      })
      .catch(() => {
        if (!requestGateRef.current.isCurrent(requestSeq)) {
          return
        }
        setSummary(null)
        setError('Load failed')
      })
      .finally(() => {
        if (requestGateRef.current.isCurrent(requestSeq)) {
          setLoading(false)
        }
      })
  }, [userId])

  const closeSummary = useCallback(() => setOpen(false), [])

  return {
    summaryOpen: open,
    summaryLoading: loading,
    summaryError: error,
    summary,
    showSummary,
    closeSummary,
  }
}

export function createSummaryRequestGate() {
  let currentSeq = 0
  return {
    next() {
      currentSeq += 1
      return currentSeq
    },
    invalidate() {
      currentSeq += 1
    },
    isCurrent(requestSeq: number) {
      return currentSeq === requestSeq
    },
  }
}
